import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import supportApiService from '../../services/supportApiService';

const SupportBookingCancellation = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [cancellations, setCancellations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCancellations, setFilteredCancellations] = useState([]);

  useEffect(() => {
    loadCancellationDetails();
  }, []);

  useEffect(() => {
    // Filter cancellations based on search term
    if (searchTerm.trim() === '') {
      setFilteredCancellations(cancellations);
    } else {
      const filtered = cancellations.filter(cancellation => 
        cancellation.cancellationId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cancellation.agentId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cancellation.hotelName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cancellation.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cancellation.confirmationNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cancellation.passengerPhoneNo?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCancellations(filtered);
    }
  }, [searchTerm, cancellations]);

  const loadCancellationDetails = async () => {
    try {
      setLoading(true);
      console.log('🔍 Loading cancellation details...');
      const data = await supportApiService.getCancellationPageDetails();
      console.log('📊 Cancellation Details API Response:', data);
      
      if (data.success) {
        setCancellations(data.detail || []);
        setFilteredCancellations(data.detail || []);
        console.log('✅ Cancellation details loaded successfully:', data.detail?.length, 'cancellations');
      } else {
        console.error('❌ Cancellation Details API returned error:', data.message);
        toast.error(data.message || 'Failed to load cancellation details');
      }
    } catch (error) {
      console.error('❌ Cancellation details load error:', error);
      toast.error('Failed to load cancellation details');
    } finally {
      setLoading(false);
    }
  };

  const handleCancellationClick = (cancellationId) => {
    // Navigate to cancellation details page
    navigate(`/support/dashboard/support/cancellation/details/${cancellationId}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  const getStatusBadge = (status) => {
    if (!status) return <span className="text-gray-500">-</span>;
    
    const statusLower = status.toLowerCase();
    let badgeClass = 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium';
    
    switch (statusLower) {
      case 'cancelled':
        badgeClass += ' bg-red-100 text-red-800';
        break;
      default:
        badgeClass += ' bg-gray-100 text-gray-800';
    }
    
    return <span className={badgeClass}>{status}</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Booking Cancellation Details</h1>
          <p className="text-gray-600">View and manage cancelled hotel bookings</p>
        </div>

        {/* Content Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {/* Card Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Booking Cancellation Details</h3>
              <div className="text-sm text-gray-500">
                Total: {filteredCancellations.length} cancellations
              </div>
            </div>
          </div>

          {/* Search Bar */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="max-w-md">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="fas fa-search text-gray-400"></i>
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-green-500 focus:border-green-500"
                  placeholder="Search by cancellation ID, hotel, agent..."
                />
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cancellation ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Agent ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Hotel Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Check-In Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Check-Out Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Address
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Phone No
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Confirmation Number
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cancellation Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Booking Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredCancellations.length > 0 ? (
                  filteredCancellations.map((cancellation, index) => (
                    <tr 
                      key={cancellation.cancellationId || index} 
                      className="hover:bg-gray-50"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {cancellation.cancellationId ? (
                          <button
                            onClick={() => handleCancellationClick(cancellation.cancellationId)}
                            className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                          >
                            {cancellation.cancellationId}
                          </button>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {cancellation.agentId || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {cancellation.hotelName || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(cancellation.checkInDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(cancellation.checkOutDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {cancellation.city || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {cancellation.passengerPhoneNo || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {cancellation.confirmationNo || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(cancellation.cancellationDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(cancellation.bookingDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {getStatusBadge(cancellation.status)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="11" className="px-6 py-8 text-center text-gray-500">
                      {searchTerm ? 'No cancellations found matching your search.' : 'No cancellation details found.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Footer */}
          {filteredCancellations.length > 0 && (
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <div className="text-sm text-gray-700">
                Showing {filteredCancellations.length} of {cancellations.length} cancellations
                {searchTerm && (
                  <span className="ml-2">
                    (filtered by "{searchTerm}")
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupportBookingCancellation;